import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Loading, Notice, Badge, Rate, rateTone, Segmented, SegmentedItem, Spinner } from './ui';
import { exchanges } from './ExchangeSelector';
import RateHistoryChart from './RateHistoryChart';
import { AnimatedNumber, riseIn } from './motion';

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${riseIn(0, 700)}

  > * {
    transition: opacity 250ms ease;
  }

  /* While a new period or pair loads, the previous result stays in place, dimmed,
     so the page keeps its height and the reader keeps their scroll position. */
  &[aria-busy='true'] > *:not(:first-child) {
    opacity: 0.45;
  }
`;

const BusyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
`;

const SummaryTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  font-size: 0.9375rem;
  color: var(--text-secondary);

  strong {
    font-size: 1.1875rem;
    font-weight: 650;
    letter-spacing: -0.02em;
    color: var(--text);
  }
`;

const PeriodMeta = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  text-align: right;

  b {
    color: var(--text);
    font-weight: 600;
  }

  small {
    display: block;
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  @media (max-width: 560px) {
    text-align: left;
  }
`;

// Flex rather than grid so a short last row stretches instead of leaving empty, tinted cells.
const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--separator);
  border: 1px solid var(--separator);
`;

const StatItem = styled.div`
  flex: 1 1 150px;
  background: var(--surface);
  padding: 16px 18px;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailsHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
`;

const DetailsTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.012em;
  color: var(--text);
`;

const DetailsList = styled.div`
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid var(--separator);
  border-radius: var(--radius-md);
  overscroll-behavior: contain;
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 16px;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    background: var(--surface-muted);
    border-bottom: 1px solid var(--separator);
  }

  td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--separator);
    color: var(--text-secondary);
  }

  tr:last-child td {
    border-bottom: 0;
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }

  tbody tr:hover td {
    background: var(--surface-muted);
  }
`;

const defaultFundingRateLocale = {
  loadingMessage: 'Loading funding rate history…',
  noDataTitle: 'No data',
  noDataMessage: 'Unable to load funding rate data',
  analysisTitlePrefix: 'Funding rate analysis',
  availablePeriodLabel: 'Available data window',
  requestedRangeLabel: 'Requested range:',
  averageLabel: 'Average rate',
  totalLabel: 'Total sum',
  recordsLabel: 'Records',
  minLabel: 'Minimum rate',
  maxLabel: 'Maximum rate',
  chartTitle: 'Funding rate over time',
  detailsTitle: 'Detailed period data',
  dateColumn: 'Date & time',
  rateColumn: 'Funding rate',
  sortLabel: 'Sort',
  sortNewest: 'Newest first',
  sortOldest: 'Oldest first',
  dateLocale: 'en-US',
};

function FundingRateResult({ tradingPair, period, fundingRateData, loading, locale = {}, exchangeId = null }) {
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' або 'oldest'
  const strings = {
    ...defaultFundingRateLocale,
    ...locale,
  };

  // Витягуємо дані з fundingRateData, якщо вони є
  const { average_funding_rate, total_funding_rate, total_records, min_rate, max_rate, data, actual_data_period } = fundingRateData || {};

  // Сортуємо дані залежно від вибраного порядку
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].sort((a, b) => {
      // Підтримка різних форматів timestamp
      const timestampA = a.timestamp || a.fundingTime || a.timepoint || a.timePoint;
      const timestampB = b.timestamp || b.fundingTime || b.timepoint || b.timePoint;
      
      if (sortOrder === 'newest') {
        return timestampB - timestampA; // Від найновіших до найстаріших
      } else {
        return timestampA - timestampB; // Від найстаріших до найновіших
      }
    });
  }, [data, sortOrder]);

  if (loading && !fundingRateData) {
    return <Loading>{strings.loadingMessage}</Loading>;
  }

  if (!fundingRateData) {
    return (
      <Notice tone="error" title={strings.noDataTitle}>
        {strings.noDataMessage}
      </Notice>
    );
  }
  const averagePercent = (average_funding_rate * 100).toFixed(6);
  const totalPercent = total_funding_rate ? (total_funding_rate * 100).toFixed(6) : null;
  const minPercent = (min_rate * 100).toFixed(6);
  const maxPercent = (max_rate * 100).toFixed(6);
  
  console.log('Funding rate data:', fundingRateData);
  console.log('Average funding rate (raw):', average_funding_rate);
  console.log('Average funding rate (%):', averagePercent);
  console.log('Data records:', data);
  if (data && data.length > 0) {
    console.log('First record:', data[0]);
    console.log('First record timestamp (OKX):', data[0].timestamp);
    console.log('First record fundingTime (Binance):', data[0].fundingTime);
    console.log('First record timepoint (KuCoin old):', data[0].timepoint);
    console.log('First record timePoint (KuCoin new):', data[0].timePoint);
    console.log('First record funding_rate (OKX):', data[0].funding_rate);
    console.log('First record fundingRate (Binance/KuCoin):', data[0].fundingRate);
  }

  const formatDate = (timestamp) => {
    const localeCode = strings.dateLocale || 'en-US';
    return new Date(timestamp).toLocaleString(localeCode, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (value) => {
    const localeCode = strings.dateLocale || 'en-US';
    return new Date(value).toLocaleDateString(localeCode);
  };

  // Values tween from what is on screen, so switching pairs or periods glides between results.
  const signed = (value) => `${value > 0 ? '+' : ''}${value.toFixed(6)}%`;

  return (
    <ResultContainer aria-busy={loading}>
      <ResultHeader>
        <SummaryTitle>
          {strings.analysisTitlePrefix}
          <strong>{tradingPair?.symbol}</strong>
          {exchangeId && <Badge>{exchanges.find((e) => e.id === exchangeId)?.name || exchangeId}</Badge>}
          {loading && (
            <BusyBadge role="status">
              <Spinner size={14} />
            </BusyBadge>
          )}
        </SummaryTitle>

        {actual_data_period && actual_data_period.start_date && actual_data_period.end_date && (
          <PeriodMeta>
            {strings.availablePeriodLabel}:{' '}
            <b>
              {formatShortDate(actual_data_period.start_date)} – {formatShortDate(actual_data_period.end_date)}
            </b>
            {period && (
              <small>
                {strings.requestedRangeLabel} {period.startDate ? formatShortDate(period.startDate) : 'N/A'} – {period.endDate ? formatShortDate(period.endDate) : 'N/A'}
              </small>
            )}
          </PeriodMeta>
        )}
      </ResultHeader>

      <StatsGrid>
        <StatItem>
          <StatLabel>{strings.averageLabel}</StatLabel>
          <StatValue>
            <Rate $tone={rateTone(average_funding_rate)}>
              <AnimatedNumber value={Number(averagePercent)} format={signed} />
            </Rate>
          </StatValue>
        </StatItem>

        {totalPercent !== null && (
          <StatItem>
            <StatLabel>{strings.totalLabel}</StatLabel>
            <StatValue>
              <Rate $tone={rateTone(total_funding_rate)}>
                <AnimatedNumber value={Number(totalPercent)} format={signed} />
              </Rate>
            </StatValue>
          </StatItem>
        )}

        <StatItem>
          <StatLabel>{strings.recordsLabel}</StatLabel>
          <StatValue>
            <AnimatedNumber value={Number(total_records) || 0} />
          </StatValue>
        </StatItem>

        <StatItem>
          <StatLabel>{strings.minLabel}</StatLabel>
          <StatValue>
            <Rate $tone={rateTone(min_rate)}>
              <AnimatedNumber value={Number(minPercent)} format={signed} />
            </Rate>
          </StatValue>
        </StatItem>

        <StatItem>
          <StatLabel>{strings.maxLabel}</StatLabel>
          <StatValue>
            <Rate $tone={rateTone(max_rate)}>
              <AnimatedNumber value={Number(maxPercent)} format={signed} />
            </Rate>
          </StatValue>
        </StatItem>
      </StatsGrid>

      {data && data.length > 1 && (
        <RateHistoryChart records={data} title={strings.chartTitle} dateLocale={strings.dateLocale} />
      )}

      {data && data.length > 0 && (
        <DetailsSection>
          <DetailsHeader>
            <DetailsTitle>{strings.detailsTitle}</DetailsTitle>
            <Segmented role="group" aria-label={strings.sortLabel}>
              <SegmentedItem
                type="button"
                $active={sortOrder === 'newest'}
                aria-pressed={sortOrder === 'newest'}
                onClick={() => setSortOrder('newest')}
              >
                {strings.sortNewest}
              </SegmentedItem>
              <SegmentedItem
                type="button"
                $active={sortOrder === 'oldest'}
                aria-pressed={sortOrder === 'oldest'}
                onClick={() => setSortOrder('oldest')}
              >
                {strings.sortOldest}
              </SegmentedItem>
            </Segmented>
          </DetailsHeader>
          <DetailsList>
            <DetailsTable>
              <thead>
                <tr>
                  <th scope="col">{strings.dateColumn}</th>
                  <th scope="col">{strings.rateColumn}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((record, index) => {
                  // Підтримка форматів: Binance (fundingTime, fundingRate), OKX (timestamp, funding_rate), KuCoin (timePoint/ts, fundingRate), Gate (timestamp, fundingRate)
                  const timestamp = record.timestamp || record.fundingTime || record.timepoint || record.timePoint || record.ts;

                  // Перевірка, чи timestamp є валідним числом
                  const isValidTimestamp = timestamp && typeof timestamp === 'number' && timestamp > 0 && timestamp > 1000000000000; // Мінімальний timestamp для 2001 року

                  const fundingRate = record.funding_rate || record.fundingRate;

                  return (
                    <tr key={index}>
                      <td>{isValidTimestamp ? formatDate(timestamp) : 'Invalid Date'}</td>
                      <td>
                        <Rate $tone={rateTone(fundingRate)}>
                          {(parseFloat(fundingRate) * 100).toFixed(6)}%
                        </Rate>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DetailsTable>
          </DetailsList>
        </DetailsSection>
      )}
    </ResultContainer>
  );
}

export default FundingRateResult;
